import { useContext, useState, useRef, useEffect } from "react";
import { ChatContext } from "../ChatContextProvider";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import CustomImage from "../CustomImage";
import { motion } from "framer-motion";
import { ArrowRightIcon, MessageSquare, Minus } from "lucide-react";

dayjs.extend(relativeTime);

const HomeWrapper = () => {
  const {
    appState,
    setAppState,
    lastMessage,
    setCurrentConversation,
    primaryColor,
    goToConversation,
    handleWindowClose,
  } = useContext(ChatContext);
  const [isMobile, setMobile] = useState(false);

  useEffect(() => {
    const applyResponsiveClasses = () => {
      if (window.parent) {
        const parentWidth = window.parent.innerWidth;

        if (parentWidth < 768) {
          setMobile(true);
        } else {
          setMobile(false);
        }
      }
    };

    applyResponsiveClasses();

    window.parent.addEventListener("resize", applyResponsiveClasses);

    return () => {
      window.parent.removeEventListener("resize", applyResponsiveClasses);
    };
  }, []);

  const seeAllConversations = () => {
    setAppState({
      ...appState,
      currentScreen: "conversations",
      previousScreen: appState.currentScreen,
    });
  };

  const openMeetingURL = () => {
    window?.open(appState?.meeting_url, "_blank")?.focus();
  };

  console.log("connect_with_sales_label", appState?.connect_with_sales_label);

  return (
    <motion.div
      className={`flex flex-col h-full w-full bg-background overflow-hidden ${
        isMobile ? "rounded-none" : "rounded-xl"
      }`}
      layoutId="chatContainer"
    >
      <div className="relative flex-1">
        <div className="relative h-full w-full">
          {appState?.background_video?.length > 0 && (
            <video
              src={appState?.background_video}
              className="object-cover absolute top-0 left-0 h-full w-full"
              autoPlay
              muted
              loop
            />
          )}
        </div>
        <div
          className={`flex absolute top-0 left-0 h-full w-full flex-col z-10 inset-0`}
          style={{
            background: `linear-gradient(to top, ${primaryColor}, transparent)`,
          }}
        >
          <div className="p-4 text-black flex justify-between items-center">
            <div className="flex items-center gap-2"></div>
            <div className="flex items-center gap-4">
              <button
                onClick={seeAllConversations}
                className="text-2xl leading-none focus:outline-none"
                aria-label="All conversations"
              >
                <MessageSquare className="size-5" />
              </button>
              <button
                onClick={() => handleWindowClose()}
                className="text-2xl leading-none focus:outline-none"
                aria-label="Close Chat"
              >
                <Minus className="size-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 flex flex-col p-4 space-y-4">
            <div
              className={`flex-1 flex flex-col ${
                appState?.background_video?.length > 0
                  ? "justify-end items-start"
                  : "justify-center items-center"
              }`}
            >
              <div className="flex justify-center items-center">
                <img className="h-10" alt="Logo" src={appState?.logo} />
              </div>
              <p className="font-medium text-xl text-white">
                {appState.welcome_message
                  ? appState.welcome_message
                  : "How can we assist you today?"}
              </p>{" "}
            </div>
            <div>
              {lastMessage && lastMessage?.messages?.length > 0 && (
                <div
                  className="flex flex-col items-start px-4 py-3 rounded-xl shadow bg-gray-50 hover:bg-gray-100 cursor-pointer space-y-2"
                  onClick={() => goToConversation({ cid: lastMessage.cid })}
                >
                  <p className="font-semibold text-sm">Recent conversation</p>
                  <div className="flex flex-1 flex-row w-full">
                    <div className="flex-shrink-0 mr-2">
                      <CustomImage
                        src={lastMessage.messages[0]?.metadata?.avatar_url}
                        alt={
                          lastMessage.messages[0].sender_type === "user"
                            ? "USER"
                            : lastMessage.messages[0]?.metadata?.first_name
                        }
                        size={38}
                      />
                    </div>
                    <div className="flex-1 flex justify-between items-center truncate">
                      <div className="flex flex-col w-2/3">
                        <p className="truncate text-ellipsis">
                          {lastMessage.messages[0].content}
                        </p>
                        <div className={`text-xs mt-1 text-left`}>
                          {`${
                            lastMessage.messages[0].sender_type === "user"
                              ? "You"
                              : lastMessage.messages[0]?.metadata?.first_name
                          } `}
                          &#x2022;{" "}
                          {dayjs(lastMessage.messages[0].createdAt).fromNow()}
                        </div>
                      </div>
                      <ArrowRightIcon className="bg-gray-200 rounded-full h-8 w-8 p-1.5" />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div
              className="hidden justify-between items-center px-4 py-3 rounded-xl shadow bg-gray-50 hover:bg-gray-100 cursor-pointer"
              onClick={() =>
                goToConversation({ conversation_purpose: "general" })
              }
            >
              <span className="text-md font-medium w-1/2">
                Send us a message
              </span>
              <div className="bg-gray-200 rounded-full h-10 w-10 p-1.5 flex justify-center items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="currentColor"
                    fillRule="evenodd"
                    d="M1.846 7.151a.75.75 0 0 0-.228 1.376l6.517 3.915l6.22-4.355a.75.75 0 0 1 .86 1.229l-6.22 4.355l1.45 7.463a.75.75 0 0 0 1.372.256L22.792 3.94a.75.75 0 0 0-.793-1.133z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col divide-y">
        <div
          className="flex justify-between items-center px-6 py-6 bg-gray-50 hover:bg-gray-100 cursor-pointer"
          onClick={() => goToConversation({ conversation_purpose: "sales" })}
        >
          <span className="text-lg font-medium w-9/12">
            {appState?.connect_with_sales_label || "Connect with sales"}
          </span>
          <ArrowRightIcon className="bg-gray-200 rounded-full h-8 w-8 p-1.5" />
        </div>
        <div
          className="flex justify-between items-center px-6 py-6 bg-gray-50 hover:bg-gray-100 border-t cursor-pointer"
          onClick={() => goToConversation({ conversation_purpose: "general" })}
        >
          <span className="text-lg font-medium w-1/2">
            {appState?.general_support_label || "General support"}
          </span>
          <ArrowRightIcon className="bg-gray-200 rounded-full h-8 w-8 p-1.5" />
        </div>
        {appState?.meeting_url && (
          <div
            onClick={() => openMeetingURL()}
            className="flex justify-between items-center px-6 py-6 bg-gray-50 hover:bg-gray-100 cursor-pointer"
          >
            <span className="text-lg font-medium w-1/2">
              {appState?.book_a_demo_label || "Schedule a Call or Demo"}
            </span>
            <ArrowRightIcon className="bg-gray-200 rounded-full h-8 w-8 p-1.5" />
          </div>
        )}
      </div>
      <a
        href="https://www.maximise.ai"
        target="_blank"
        className="p-1 flex justify-center items-center bg-gray-100 gap-1 font-medium text-xs"
      >
        <span>Powered by</span>
        <span className="font-semibold">Maximise.ai</span>
      </a>
    </motion.div>
  );
};

export default HomeWrapper;
